import { useState, useMemo } from "react";
import { useAllImagesQuery, useDeleteImage, useRenameImage, useImageQuotaQuery } from "../../../lib/api/images.api";
import { apiFetchBlob } from "../../../lib/api/client";
import { ImageThumbnail } from "../../images/components/ImageThumbnail";
import { ImageLightbox } from "../../images/components/ImageLightbox";
import { SectionLabel } from "./SettingsUIComponents";
import type { GalleryImageDTO } from "../../../shared/schemas";

type FilterMode = "all" | "task" | "event" | "orphan";

function formatSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} o`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} Ko`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} Mo`;
}

function GalleryItem({
  image,
  onFullscreen,
}: {
  readonly image: GalleryImageDTO;
  readonly onFullscreen: (id: string) => void;
}) {
  const deleteMutation = useDeleteImage();
  const renameMutation = useRenameImage();
  const [editing, setEditing] = useState(false);
  const [name, setName] = useState(image.originalName);

  const handleRename = () => {
    if (name.trim() && name.trim() !== image.originalName) {
      renameMutation.mutate({ id: image.id, originalName: name.trim() });
    }
    setEditing(false);
  };

  const handleDownload = async (format?: "webp") => {
    const query = format ? "?format=webp" : "";
    const blob = await apiFetchBlob(`/images/download/${image.id}${query}`);
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = format
      ? image.originalName.replace(/\.[^.]+$/, ".webp")
      : image.originalName;
    a.click();
    URL.revokeObjectURL(url);
  };

  const entityLabel = image.taskId
    ? `Tâche : ${image.entityName ?? "Sans titre"}`
    : image.eventId
      ? `Événement : ${image.entityName ?? "Sans titre"}`
      : "Non rattachée";

  return (
    <div className="flex items-center gap-3 py-2 border-b border-border/40 last:border-b-0">
      <ImageThumbnail
        imageId={image.id}
        alt={image.originalName}
        className="h-12 w-12 object-cover rounded shrink-0"
        onClick={() => onFullscreen(image.id)}
      />
      <div className="flex-1 min-w-0">
        {editing ? (
          <input
            type="text"
            value={name}
            onChange={(e) => setName(e.target.value)}
            onBlur={handleRename}
            onKeyDown={(e) => {
              if (e.key === "Enter") handleRename();
              if (e.key === "Escape") { setName(image.originalName); setEditing(false); }
            }}
            className="w-full text-xs bg-surface-alt border border-border rounded px-2 py-1 text-ink outline-none focus:border-accent"
            autoFocus
          />
        ) : (
          <p className="text-xs font-medium text-ink truncate">{image.originalName}</p>
        )}
        <p className="text-[10px] text-ink-muted truncate">
          {entityLabel} · {formatSize(image.size)}
        </p>
      </div>
      <div className="flex items-center gap-1 shrink-0">
        <button
          type="button"
          onClick={() => { setName(image.originalName); setEditing(true); }}
          className="p-1.5 text-ink-faint hover:text-ink rounded hover:bg-surface-alt transition-colors"
          title="Renommer"
        >
          <svg viewBox="0 0 24 24" className="w-3.5 h-3.5" stroke="currentColor" strokeWidth={2} fill="none">
            <path d="M17 3a2.85 2.83 0 1 1 4 4L7.5 20.5 2 22l1.5-5.5Z" />
          </svg>
        </button>
        <button
          type="button"
          onClick={() => handleDownload()}
          className="p-1.5 text-ink-faint hover:text-ink rounded hover:bg-surface-alt transition-colors"
          title={`Télécharger (${image.mimeType.split("/")[1]?.toUpperCase()})`}
        >
          <svg viewBox="0 0 24 24" className="w-3.5 h-3.5" stroke="currentColor" strokeWidth={2} fill="none">
            <path d="M21 15v4a2 2 0 01-2 2H5a2 2 0 01-2-2v-4M7 10l5 5 5-5M12 15V3" />
          </svg>
        </button>
        {image.mimeType !== "image/webp" && (
          <button
            type="button"
            onClick={() => handleDownload("webp")}
            className="p-1.5 text-[9px] font-bold text-ink-faint hover:text-ink rounded hover:bg-surface-alt transition-colors leading-none"
            title="Télécharger en WebP"
          >
            WP
          </button>
        )}
        <button
          type="button"
          onClick={() => deleteMutation.mutate(image.id)}
          className="p-1.5 text-ink-faint hover:text-danger rounded hover:bg-danger/10 transition-colors"
          title="Supprimer"
        >
          <svg viewBox="0 0 24 24" className="w-3.5 h-3.5" stroke="currentColor" strokeWidth={2} fill="none">
            <path d="M3 6h18M19 6v14a2 2 0 01-2 2H7a2 2 0 01-2-2V6M8 6V4a2 2 0 012-2h4a2 2 0 012 2v2" />
          </svg>
        </button>
      </div>
    </div>
  );
}

export function GallerySection() {
  const { data: images, isLoading } = useAllImagesQuery();
  const { data: quota } = useImageQuotaQuery();
  const [filter, setFilter] = useState<FilterMode>("all");
  const [fullscreenId, setFullscreenId] = useState<string | null>(null);

  const filtered = useMemo(() => {
    if (!images) return [];
    switch (filter) {
      case "task": return images.filter((i) => i.taskId);
      case "event": return images.filter((i) => i.eventId);
      case "orphan": return images.filter((i) => !i.taskId && !i.eventId);
      default: return images;
    }
  }, [images, filter]);

  const counts = useMemo(() => {
    if (!images) return { all: 0, task: 0, event: 0, orphan: 0 };
    return {
      all: images.length,
      task: images.filter((i) => i.taskId).length,
      event: images.filter((i) => i.eventId).length,
      orphan: images.filter((i) => !i.taskId && !i.eventId).length,
    };
  }, [images]);

  if (isLoading) {
    return <p className="text-xs text-ink-muted py-4 text-center">Chargement...</p>;
  }

  const filters: { id: FilterMode; label: string; count: number }[] = [
    { id: "all", label: "Toutes", count: counts.all },
    { id: "task", label: "Tâches", count: counts.task },
    { id: "event", label: "Événements", count: counts.event },
    { id: "orphan", label: "Non rattachées", count: counts.orphan },
  ];

  return (
    <div className="space-y-3">
      {quota && (
        <div className="space-y-1.5">
          <SectionLabel>Stockage</SectionLabel>
          <div className="flex items-center gap-2">
            <div className="flex-1 h-2 bg-surface-alt rounded-full overflow-hidden">
              <div
                className="h-full bg-accent rounded-full transition-all"
                style={{ width: `${Math.min((quota.used / quota.limit) * 100, 100)}%` }}
              />
            </div>
            <span className="text-[10px] text-ink-muted shrink-0">
              {formatSize(quota.used)} / {formatSize(quota.limit)}
            </span>
          </div>
        </div>
      )}

      <div>
        <SectionLabel>Filtrer</SectionLabel>
        <div className="flex gap-1 mt-1.5">
          {filters.map((f) => (
            <button
              key={f.id}
              type="button"
              onClick={() => setFilter(f.id)}
              className={`px-2 py-1 text-[11px] rounded-md transition-colors ${
                filter === f.id
                  ? "bg-accent text-white"
                  : "bg-surface-alt text-ink-muted hover:text-ink"
              }`}
            >
              {f.label} ({f.count})
            </button>
          ))}
        </div>
      </div>

      <div>
        {filtered.length === 0 ? (
          <p className="text-xs text-ink-faint py-4 text-center">Aucune image</p>
        ) : (
          <div className="max-h-80 overflow-y-auto scrollbar-custom">
            {filtered.map((img) => (
              <GalleryItem key={img.id} image={img} onFullscreen={setFullscreenId} />
            ))}
          </div>
        )}
      </div>

      {fullscreenId && (
        <ImageLightbox imageId={fullscreenId} onClose={() => setFullscreenId(null)} />
      )}
    </div>
  );
}
